import { useEffect, useMemo, useState } from "react";
import {
  Alert,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  FormHelperText,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { useTranslation } from "i18n-fe";
import { userDisplayName } from "common-fe";
import {
  ERROR_DEDUCTION_SCHEMES,
  DSHB_CLASS_KINDS,
  RESULT_LAYOUTS,
  resolveResultLayout,
} from "score-common";

// #2421 — grew from #1055's scribe-only bulk dialog into one bulk CLASS
// CONFIG dialog covering every per-class option apps/score-api/src/handlers/
// class-config.js exposes a setter for, alongside the original per-position
// scribe pickers. `setClassPlacings` (results.js) is deliberately NOT
// included: it batch-commits per-RIDE placings gated on a releasability
// check, not a scalar class-level field, so it does not fit this dialog's
// agree/mixed/not-applicable-per-class model — AC1 scopes this dialog to
// class-config.js's own setters.
//
// #1783 — same equitation-only gate ClassDetailDrawer's walk-trot Switch
// uses (prod check: 19/19 dressage-seat equitation classes carry sectionCode
// "6900", 0/380 non-equitation classes do). Duplicated here (not imported
// from the drawer) rather than adding a cross-file dependency between two
// already-large, independently-owned components for one literal.
const EQUITATION_SECTION_CODE = "6900";

// Sentinels for a Select's "nothing decided yet" closed-state label, distinct
// from every real value the field can hold (mirrors ClassDetailDrawer's own
// SCHEME_NOT_SET precedent: MUI's Select shrinks the floating label only for
// a non-"" value, so `value=""` cannot double as a placeholder here).
const NOT_SET = "__NOT_SET__";
const MIXED = "__MIXED__";
const NOT_APPLICABLE = "__NOT_APPLICABLE__";
// dshbKindOverride's "clear back to the guess" value. Exported (defect 2's
// fix) so the write side (ClassManager's bulkSaveClassConfig) can map this
// sentinel back to `null` before the PUT — the raw sentinel string is not a
// valid dshbClassKindOverride and 400s dshbKindOverrideBodySchema's enum on
// every selected class.
export const DSHB_KIND_OVERRIDE_AUTO = "__AUTO__";
const AUTO = DSHB_KIND_OVERRIDE_AUTO;

const ERROR_DEDUCTION_SCHEME_LABEL_KEYS = {
  [ERROR_DEDUCTION_SCHEMES.POINTS]: "ring.errorDeductionSchemePoints",
  [ERROR_DEDUCTION_SCHEMES.PERCENT_SENIOR]: "ring.errorDeductionSchemePercentSenior",
  [ERROR_DEDUCTION_SCHEMES.PERCENT_YOUTH]: "ring.errorDeductionSchemePercentYouth",
};

// Exported so a test can pin every DSHB_CLASS_KINDS member to a real label
// key here (#2729 judge finding: this dialog keeps its own copy of the map
// ClassDetailDrawer also has, and a member missing from THIS copy renders a
// blank, still-selectable option via Object.values(DSHB_CLASS_KINDS).map
// below, silently writing that value to every selected class).
export const DSHB_KIND_OVERRIDE_LABEL_KEYS = {
  [DSHB_CLASS_KINDS.CHAMPIONSHIP]: "ring.dshbKindOverrideChampionship",
  [DSHB_CLASS_KINDS.USDFBC_CHAMPIONSHIP]: "ring.dshbKindOverrideUsdfbcChampionship",
  [DSHB_CLASS_KINDS.MATERIALE]: "ring.dshbKindOverrideMateriale",
  [DSHB_CLASS_KINDS.UNDER_SADDLE]: "ring.dshbKindOverrideUnderSaddle",
  [DSHB_CLASS_KINDS.SUITABILITY]: "ring.dshbKindOverrideSuitability",
  // #2729 — this dialog keeps its own copy of the label map (separate from
  // ClassDetailDrawer's), so a new DSHB_CLASS_KINDS member has to be added
  // here too or Object.values(DSHB_CLASS_KINDS).map(...) below renders a
  // blank, still-selectable option that silently writes the new enum value
  // to every selected class (found by judge review on this exact card).
  [DSHB_CLASS_KINDS.NOT_DSHB]: "ring.dshbKindOverrideNotDshb",
};

function isYoungHorseApplicable(c, isYoungHorse) {
  return Boolean(isYoungHorse?.(c));
}

function isEquitation(c) {
  return c?.sectionCode === EQUITATION_SECTION_CODE;
}

// The card's four-state read: given the SELECTED classes, an applicability
// predicate, and a value reader, resolve one of:
//   - { state: "na" }            no selected class this field applies to
//   - { state: "agree", value }  every applicable class already agrees
//   - { state: "mixed" }         applicable classes disagree
// Never a blank and never "one class's value pretending to be everyone's"
// (card's own wording) — na/mixed are distinct terminal states, not a value.
function fieldState(classes, applyFn, getValue) {
  const applicable = classes.filter(applyFn);
  if (applicable.length === 0) return { state: "na" };
  const first = getValue(applicable[0]);
  const allAgree = applicable.every((c) => getValue(c) === first);
  return allAgree ? { state: "agree", value: first } : { state: "mixed" };
}

export default function BulkAssignScribesDialog({
  open,
  onClose,
  classes = [],
  positions,
  scribes,
  judges = [],
  allJudges = [],
  isYoungHorse = () => false,
  saving,
  error,
  partialFailure = null,
  // #2519: class-field pairs the caller's own guards (topology gate,
  // fault-time-limit bulk-clear) deliberately did not write, distinct from
  // partialFailure: no write was ever attempted for these, so they never
  // show up as a failure. Without this they vanished with no trace at all.
  skipped = null,
  onSubmit,
}) {
  const { t } = useTranslation("scoreadmin");
  const classCount = classes.length;

  // dirty[key] === true once the operator has actually interacted with that
  // field, regardless of what value they land on — the ONLY thing that
  // decides whether a field gets written (card's "write rule" / AC5-6).
  // values[key] holds what they picked. Compound fields (qualityJudging,
  // sectionJudgeAssignments) get one dirty/value entry PER SUB-FIELD so
  // e.g. touching topology never implies touching qualityJudge.
  const [dirty, setDirty] = useState({});
  const [values, setValues] = useState({});
  // A field currently showing "mixed" that the operator just interacted
  // with, pending the one-time warning confirm (AC7). Cleared (without
  // committing) on Cancel; committed into dirty/values on Continue.
  const [pendingMixed, setPendingMixed] = useState(null);
  const [confirmSubmitOpen, setConfirmSubmitOpen] = useState(false);
  // #2421 repair — BulkNumberField (unlike BulkSelectField's fully-controlled
  // Select) buffers its typed text in its own local state, so cancelling the
  // mixed-field warning left the typed digits on screen with nothing dirty
  // behind them: a lie about what will be written. This is a one-shot signal
  // ({ key, token }, token always fresh so the same key can cancel twice in a
  // row) telling the one BulkNumberField instance whose fieldKey matches to
  // revert its buffer back to "Mixed", mirroring the Select path.
  const [mixedCancelSignal, setMixedCancelSignal] = useState(null);

  useEffect(() => {
    if (open) {
      setDirty({});
      setValues({});
      setPendingMixed(null);
      setConfirmSubmitOpen(false);
      setMixedCancelSignal(null);
    }
  }, [open]);

  const menuProps = { sx: { zIndex: (theme) => theme.zIndex.modal + 200 } };

  // ---- per-field state, computed once per render off the current selection ----
  const scribeStates = useMemo(
    () =>
      Object.fromEntries(
        positions.map((p) => [
          p,
          fieldState(
            classes,
            (c) => positionsForClassInDialog(c, p),
            (c) => scribeUserId(c, p),
          ),
        ]),
      ),
    [classes, positions],
  );
  const faultTimeLimitState = useMemo(
    () => fieldState(classes, () => true, (c) => c.faultTimeLimitSeconds ?? null),
    [classes],
  );
  const errorDeductionSchemeState = useMemo(
    () => fieldState(classes, () => true, (c) => c.errorDeductionScheme ?? null),
    [classes],
  );
  const skipUsefUploadState = useMemo(
    () => fieldState(classes, () => true, (c) => c.skipUsefUpload === true),
    [classes],
  );
  // #2421 repair (item 7) — resolveResultLayout is score-common's single
  // fails-closed resolver every reader of a stored resultLayout must go
  // through (unset/unrecognised both collapse to BY_DIVISION); this used to
  // hand-roll the exact same semantics inline instead of importing it.
  const resultLayoutState = useMemo(
    () => fieldState(classes, () => true, (c) => resolveResultLayout(c.resultLayout)),
    [classes],
  );
  const dshbKindOverrideState = useMemo(
    () => fieldState(classes, () => true, (c) => c.dshbClassKindOverride ?? null),
    [classes],
  );
  const walkTrotOnlyState = useMemo(
    () => fieldState(classes, isEquitation, (c) => c.walkTrotOnly === true),
    [classes],
  );
  const paperEntryState = useMemo(
    () => fieldState(classes, () => true, (c) => c.paperEntry === true),
    [classes],
  );
  const qjTopologyState = useMemo(
    () =>
      fieldState(
        classes,
        (c) => isYoungHorseApplicable(c, isYoungHorse),
        (c) => c.qualityJudging?.topology ?? "",
      ),
    [classes, isYoungHorse],
  );
  const qjPositionState = useMemo(
    () =>
      fieldState(
        classes,
        (c) => isYoungHorseApplicable(c, isYoungHorse),
        (c) => c.qualityJudging?.qualityPosition ?? "",
      ),
    [classes, isYoungHorse],
  );
  const qjJudgeState = useMemo(
    () =>
      fieldState(
        classes,
        (c) => isYoungHorseApplicable(c, isYoungHorse),
        (c) => c.qualityJudging?.qualityJudge ?? "",
      ),
    [classes, isYoungHorse],
  );
  const sectionTechnicalState = useMemo(
    () =>
      fieldState(
        classes,
        (c) => isYoungHorseApplicable(c, isYoungHorse),
        (c) => c.sectionJudgeAssignments?.technical ?? "",
      ),
    [classes, isYoungHorse],
  );
  const sectionQualityState = useMemo(
    () =>
      fieldState(
        classes,
        (c) => isYoungHorseApplicable(c, isYoungHorse),
        (c) => c.sectionJudgeAssignments?.quality ?? "",
      ),
    [classes, isYoungHorse],
  );

  // Commit a change straight away (agree/na fields — no warning needed).
  function commit(key, value) {
    setDirty((d) => ({ ...d, [key]: true }));
    setValues((v) => ({ ...v, [key]: value }));
  }

  // Every field's onChange goes through this. `state` is that field's
  // current fieldState() result. A field already dirty never re-warns (the
  // card's "moment" is the FIRST touch); a field currently "mixed" that
  // hasn't been touched yet stages the change behind the confirm dialog
  // instead of committing it immediately (AC7). An "agree" or already-dirty
  // field commits with no warning (AC8).
  function handleChange(key, label, value, state) {
    if (state === "mixed" && !dirty[key]) {
      setPendingMixed({ key, label, value });
      return;
    }
    commit(key, value);
  }

  function confirmPendingMixed() {
    if (pendingMixed) commit(pendingMixed.key, pendingMixed.value);
    setPendingMixed(null);
  }
  function cancelPendingMixed() {
    if (pendingMixed) setMixedCancelSignal({ key: pendingMixed.key, token: Date.now() });
    setPendingMixed(null);
  }

  // ---- the list of fields that WILL change, for the submit confirmation ----
  const changedFieldLabels = useMemo(() => {
    const labels = [];
    for (const p of positions) {
      if (dirty[`scribe:${p}`]) labels.push(t("ring.positionScribe", { position: p }));
    }
    if (dirty.faultTimeLimitSeconds) labels.push(t("ring.faultTimeLimit"));
    if (dirty.errorDeductionScheme) labels.push(t("ring.errorDeductionScheme"));
    if (dirty.skipUsefUpload) labels.push(t("ring.bulkAssign.skipUsefUpload"));
    if (dirty.resultLayout) labels.push(t("ring.bulkAssign.resultLayout"));
    if (dirty.dshbKindOverride) labels.push(t("ring.dshbKindOverride"));
    if (dirty.walkTrotOnly) labels.push(t("ring.walkTrotOnly"));
    if (dirty.paperEntry) labels.push(t("ring.paperEntry"));
    if (dirty["qualityJudging.topology"] || dirty["qualityJudging.qualityPosition"] || dirty["qualityJudging.qualityJudge"]) {
      labels.push(t("tq.heading"));
    }
    if (dirty["sectionJudgeAssignments.technical"] || dirty["sectionJudgeAssignments.quality"]) {
      labels.push(t("sectionJudge.heading"));
    }
    return labels;
  }, [dirty, positions, t]);

  const hasAnyChange = changedFieldLabels.length > 0;

  function handleSubmitClick() {
    setConfirmSubmitOpen(true);
  }
  function handleConfirmSubmit() {
    setConfirmSubmitOpen(false);
    onSubmit({ dirty, values });
  }

  // AC4 — "not applicable" must be a visible, LABELED state, never a hidden
  // section: the T/Q and section-judge groups below always render, even with
  // zero young-horse classes selected, so the operator sees WHY nothing is
  // offered there (each field's own BulkSelectField "na" branch) instead of
  // the whole block silently vanishing.

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      data-testid="bulk-assign-scribes-dialog"
      sx={{ zIndex: (theme) => theme.zIndex.modal + 100 }}
    >
      <DialogTitle>
        <Typography variant="h6" color="text.primary">
          {t("ring.bulkAssign.title", { count: classCount })}
        </Typography>
      </DialogTitle>
      <DialogContent>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          {t("ring.bulkAssign.help")}
        </Typography>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }} data-testid="bulk-assign-error">
            {error.message || t("ring.loadError")}
          </Alert>
        )}

        {partialFailure && partialFailure.failures.length > 0 && (
          <Alert severity="warning" sx={{ mb: 2 }} data-testid="bulk-assign-partial-failure">
            {
              // #2421 repair — "everything else you changed was still
              // applied" is only true when SOME write got through. When
              // every attempted write failed, say so instead of claiming a
              // partial success that didn't happen.
              partialFailure.failures.length < partialFailure.totalWrites
                ? t("ring.bulkAssign.partialFailure", { count: partialFailure.failures.length })
                : t("ring.bulkAssign.allFailure", { count: partialFailure.failures.length })
            }
            <ul style={{ margin: "4px 0 0", paddingLeft: 20 }}>
              {partialFailure.failures.map((f) => (
                <li key={`${f.classId}-${f.field}`}>
                  {t("ring.bulkAssign.partialFailureRow", { label: f.label, field: f.field })}
                </li>
              ))}
            </ul>
          </Alert>
        )}

        {skipped && skipped.length > 0 && (
          <Alert severity="warning" sx={{ mb: 2 }} data-testid="bulk-assign-skipped">
            {t("ring.bulkAssign.skipped", { count: skipped.length })}
            <ul style={{ margin: "4px 0 0", paddingLeft: 20 }}>
              {skipped.map((s) => (
                <li key={`${s.classId}-${s.field}`}>
                  {t("ring.bulkAssign.skippedRow", { label: s.label, field: s.field })}
                  {s.reason ? `: ${s.reason}` : ""}
                </li>
              ))}
            </ul>
          </Alert>
        )}

        <Typography variant="overline" color="text.secondary">
          {t("ring.classConfigJudgingHeading")}
        </Typography>
        <Stack spacing={2} sx={{ mt: 1, mb: 2 }}>
          {positions.length === 0 ? (
            <Typography variant="body2" color="text.secondary">
              {t("ring.bulkAssign.noPositions")}
            </Typography>
          ) : (
            <Stack direction="row" flexWrap="wrap" gap={2}>
              {positions.map((position) => {
                const key = `scribe:${position}`;
                const st = scribeStates[position];
                const label = t("ring.positionScribe", { position });
                const shown = dirty[key] ? values[key] : st.state === "agree" ? st.value ?? "" : "";
                return (
                  <FormControl key={position} size="small" sx={{ width: 240 }} data-testid={`bulk-config-scribe-${position}`}>
                    <InputLabel id={`bulk-${position}-label`}>{label}</InputLabel>
                    <Select
                      labelId={`bulk-${position}-label`}
                      label={label}
                      value={st.state === "mixed" && !dirty[key] ? MIXED : shown}
                      disabled={saving}
                      data-testid={`bulk-assign-select-${position}`}
                      MenuProps={menuProps}
                      onChange={(e) => handleChange(key, label, e.target.value, st.state)}
                    >
                      {st.state === "mixed" && !dirty[key] && (
                        <MenuItem value={MIXED} disabled>
                          <em>{t("ring.bulkAssign.mixed")}</em>
                        </MenuItem>
                      )}
                      <MenuItem value="">
                        <em>{t("ring.bulkAssign.clear")}</em>
                      </MenuItem>
                      {scribes.map((m) => (
                        <MenuItem key={m.userId} value={m.userId}>
                          {userDisplayName(m)}
                        </MenuItem>
                      ))}
                    </Select>
                    <FormHelperText>{t("ring.bulkAssign.fieldHelp")}</FormHelperText>
                  </FormControl>
                );
              })}
            </Stack>
          )}

          <Stack direction="row" flexWrap="wrap" gap={2}>
            <BulkNumberField
              testIdBase="fault-time-limit"
              label={t("ring.faultTimeLimit")}
              help={t("ring.faultTimeLimitHelp")}
              fieldKey="faultTimeLimitSeconds"
              state={faultTimeLimitState}
              dirty={dirty}
              values={values}
              mixedCancelSignal={mixedCancelSignal}
              saving={saving}
              onCommit={handleChange}
            />

            <BulkSelectField
              testIdBase="error-deduction-scheme"
              label={t("ring.errorDeductionScheme")}
              help={t("ring.errorDeductionSchemeHelp")}
              fieldKey="errorDeductionScheme"
              state={errorDeductionSchemeState}
              dirty={dirty}
              values={values}
              saving={saving}
              menuProps={menuProps}
              onCommit={handleChange}
              t={t}
              notSetLabelKey="ring.errorDeductionSchemeNotSet"
              options={Object.values(ERROR_DEDUCTION_SCHEMES).map((scheme) => ({
                value: scheme,
                label: t(ERROR_DEDUCTION_SCHEME_LABEL_KEYS[scheme]),
              }))}
            />
          </Stack>

          <Typography variant="body2" color="text.secondary">
            {t("tq.help")}
          </Typography>
          <Stack direction="row" flexWrap="wrap" gap={2}>
            <BulkSelectField
              testIdBase="tq-topology"
              label={t("tq.topology")}
              help={t("tq.topologyHelp")}
              fieldKey="qualityJudging.topology"
              state={qjTopologyState}
              dirty={dirty}
              values={values}
              saving={saving}
              menuProps={menuProps}
              onCommit={handleChange}
              t={t}
              allowBlank
              blankLabel={t("ring.none")}
              options={[
                { value: "two-judge", label: t("tq.twoJudge") },
                { value: "technical-only", label: t("tq.technicalOnly") },
              ]}
            />
            <BulkSelectField
              testIdBase="tq-position"
              label={t("tq.qualityPosition")}
              help={t("tq.qualityPositionHelp")}
              fieldKey="qualityJudging.qualityPosition"
              state={qjPositionState}
              dirty={dirty}
              values={values}
              saving={saving}
              menuProps={menuProps}
              onCommit={handleChange}
              t={t}
              allowBlank
              blankLabel={t("ring.none")}
              width={160}
              options={[
                { value: "E", label: "E" },
                { value: "B", label: "B" },
              ]}
            />
            <BulkSelectField
              testIdBase="tq-judge"
              label={t("tq.qualityJudge")}
              help={t("tq.qualityJudgeHelp")}
              fieldKey="qualityJudging.qualityJudge"
              state={qjJudgeState}
              dirty={dirty}
              values={values}
              saving={saving}
              menuProps={menuProps}
              onCommit={handleChange}
              t={t}
              allowBlank
              blankLabel={t("tq.selectJudge")}
              // #2421 repair (defect 1): the topology block's quality
              // judge is stored as an FVD panel NAME string (score-api's
              // normalizeQualityJudging does `name = qualityJudge.trim()`,
              // packages/score-common/src/schemas/validation.js's
              // qualityJudge is a bare nullable string) — NOT a
              // score-judge roster id, unlike the section-judge fields
              // right below. `allJudges` (ClassManager's own name set
              // harvested from judgeAssignments, same source
              // ClassDetailDrawer's own tq-judge picker uses) is the
              // correct options source here, value===label, not `judges`.
              options={(allJudges || []).map((name) => ({ value: name, label: name }))}
            />
          </Stack>

          <Stack direction="row" flexWrap="wrap" gap={2}>
            <BulkSelectField
              testIdBase="section-judge-technical"
              label={t("sectionJudge.technical")}
              help={t("sectionJudge.help")}
              fieldKey="sectionJudgeAssignments.technical"
              state={sectionTechnicalState}
              dirty={dirty}
              values={values}
              saving={saving}
              menuProps={menuProps}
              onCommit={handleChange}
              t={t}
              allowBlank
              blankLabel={t("sectionJudge.selectJudge")}
              options={(judges || []).map((j) => ({ value: j.id, label: userDisplayName(j) || j.id }))}
            />
            <BulkSelectField
              testIdBase="section-judge-quality"
              label={t("sectionJudge.quality")}
              fieldKey="sectionJudgeAssignments.quality"
              state={sectionQualityState}
              dirty={dirty}
              values={values}
              saving={saving}
              menuProps={menuProps}
              onCommit={handleChange}
              t={t}
              allowBlank
              blankLabel={t("sectionJudge.selectJudge")}
              options={(judges || []).map((j) => ({ value: j.id, label: userDisplayName(j) || j.id }))}
            />
          </Stack>
        </Stack>

        <Divider sx={{ my: 2 }} />
        <Typography variant="overline" color="text.secondary">
          {t("ring.classConfigModeHeading")}
        </Typography>
        <Stack direction="row" flexWrap="wrap" gap={2} sx={{ mt: 1, mb: 2 }}>
          <BulkBooleanField
            testIdBase="walk-trot-only"
            label={t("ring.walkTrotOnly")}
            help={t("ring.walkTrotOnlyHelp")}
            fieldKey="walkTrotOnly"
            state={walkTrotOnlyState}
            dirty={dirty}
            values={values}
            saving={saving}
            menuProps={menuProps}
            onCommit={handleChange}
            t={t}
          />
          <BulkBooleanField
            testIdBase="paper-entry"
            label={t("ring.paperEntry")}
            help={t("ring.paperEntryHelp")}
            fieldKey="paperEntry"
            state={paperEntryState}
            dirty={dirty}
            values={values}
            saving={saving}
            menuProps={menuProps}
            onCommit={handleChange}
            t={t}
          />
        </Stack>
        {
          // #2421 (repair item 9, decision recorded on the card rather than
          // asked): paperEntry stamps onto a ScoreSheet only at CREATION
          // (score-api's isPaperEntryClass/createScoreSheet), never
          // retroactively, so bulk-flipping it across a day's classes
          // silently no-ops on any class already scored — the exact
          // "existing sheets are untouched" gap ClassDetailDrawer already
          // warns about for a single class. Reusing that same warning here,
          // the moment the operator touches this field, is matching the
          // drawer's existing behavior, not a new product choice; there is
          // no per-class "already has sheets" signal available in bulk
          // (that check is a per-class rides+sheets fetch the drawer does
          // lazily on open), so this shows unconditionally rather than
          // fetching it for every selected class.
          dirty.paperEntry && (
            <Alert severity="warning" sx={{ mb: 2 }} data-testid="bulk-paper-entry-existing-sheets-warning">
              {t("ring.paperEntryExistingSheetsWarning")}
            </Alert>
          )
        }

        <Divider sx={{ my: 2 }} />
        <Typography variant="overline" color="text.secondary">
          {t("ring.classConfigResultsHeading")}
        </Typography>
        <Stack direction="row" flexWrap="wrap" gap={2} sx={{ mt: 1 }}>
          <BulkBooleanField
            testIdBase="skip-usef-upload"
            label={t("ring.bulkAssign.skipUsefUpload")}
            help={t("ring.bulkAssign.skipUsefUploadHelp")}
            fieldKey="skipUsefUpload"
            state={skipUsefUploadState}
            dirty={dirty}
            values={values}
            saving={saving}
            menuProps={menuProps}
            onCommit={handleChange}
            t={t}
          />
          <BulkSelectField
            testIdBase="result-layout"
            label={t("ring.bulkAssign.resultLayout")}
            help={t("ring.bulkAssign.resultLayoutHelp")}
            fieldKey="resultLayout"
            state={resultLayoutState}
            dirty={dirty}
            values={values}
            saving={saving}
            menuProps={menuProps}
            onCommit={handleChange}
            t={t}
            options={[
              { value: RESULT_LAYOUTS.BY_DIVISION, label: t("ring.bulkAssign.resultLayoutByDivision") },
              { value: RESULT_LAYOUTS.COMBINED, label: t("ring.bulkAssign.resultLayoutCombined") },
            ]}
          />
          <BulkSelectField
            testIdBase="dshb-kind-override"
            label={t("ring.dshbKindOverride")}
            help={t("ring.dshbKindOverrideHelp")}
            fieldKey="dshbKindOverride"
            state={dshbKindOverrideState}
            dirty={dirty}
            values={values}
            saving={saving}
            menuProps={menuProps}
            onCommit={handleChange}
            t={t}
            notSetValue={AUTO}
            notSetLabelKey="ring.dshbKindOverrideAuto"
            notSetSelectable
            options={Object.values(DSHB_CLASS_KINDS).map((kind) => ({
              value: kind,
              label: t(DSHB_KIND_OVERRIDE_LABEL_KEYS[kind]),
            }))}
          />
        </Stack>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 3 }}>
        <Button onClick={onClose} disabled={saving} sx={{ color: "text.primary" }}>
          {t("ring.bulkAssign.cancel")}
        </Button>
        <Button
          variant="contained"
          onClick={handleSubmitClick}
          disabled={saving || !hasAnyChange}
          data-testid="bulk-assign-scribes-submit"
        >
          {t("ring.bulkAssign.assign")}
        </Button>
      </DialogActions>

      {/* AC7 — one-time warning the moment a MIXED field is changed. */}
      <Dialog
        open={Boolean(pendingMixed)}
        onClose={cancelPendingMixed}
        data-testid="bulk-mixed-warning-dialog"
        sx={{ zIndex: (theme) => theme.zIndex.modal + 300 }}
      >
        <DialogTitle>{t("ring.bulkAssign.mixedWarningTitle")}</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.primary">
            {t("ring.bulkAssign.mixedWarningBody", { field: pendingMixed?.label, count: classCount })}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={cancelPendingMixed} sx={{ color: "text.primary" }} data-testid="bulk-mixed-warning-cancel">
            {t("ring.bulkAssign.cancel")}
          </Button>
          <Button variant="contained" onClick={confirmPendingMixed} data-testid="bulk-mixed-warning-continue">
            {t("ring.bulkAssign.mixedWarningContinue")}
          </Button>
        </DialogActions>
      </Dialog>

      {/* AC9 — one confirmation on submit, naming the class count and the
          fields that will change. */}
      <Dialog
        open={confirmSubmitOpen}
        onClose={() => setConfirmSubmitOpen(false)}
        data-testid="bulk-submit-confirm-dialog"
        sx={{ zIndex: (theme) => theme.zIndex.modal + 300 }}
      >
        <DialogTitle>{t("ring.bulkAssign.confirmSubmitTitle", { count: classCount })}</DialogTitle>
        <DialogContent>
          <ul data-testid="bulk-submit-confirm-fields">
            {changedFieldLabels.map((label) => (
              <li key={label}>{label}</li>
            ))}
          </ul>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmSubmitOpen(false)} sx={{ color: "text.primary" }}>
            {t("ring.bulkAssign.cancel")}
          </Button>
          <Button
            variant="contained"
            onClick={handleConfirmSubmit}
            disabled={saving}
            data-testid="bulk-submit-confirm-button"
          >
            {t("ring.bulkAssign.confirmSubmitButton", { count: classCount })}
          </Button>
        </DialogActions>
      </Dialog>
    </Dialog>
  );
}

function positionsForClassInDialog(c, position) {
  return Array.isArray(c.judgeAssignments?.[position]) && c.judgeAssignments[position].length > 0;
}
function scribeUserId(c, position) {
  const ids = c.scribeAssignments?.[position];
  return Array.isArray(ids) && ids.length ? ids[0] : null;
}

// A plain numeric field with the same four-state behavior as the Selects
// below, via a local typed buffer (matches ClassDetailDrawer's own
// fault-time-limit field: onBlur is a numeric TextField's equivalent of a
// Select's onChange "commit").
function BulkNumberField({
  testIdBase,
  label,
  help,
  fieldKey,
  state,
  dirty,
  values,
  saving,
  onCommit,
  mixedCancelSignal,
}) {
  const isDirty = Boolean(dirty[fieldKey]);
  const displayDefault = state.state === "agree" && state.value != null ? String(state.value) : "";
  const [text, setText] = useState(isDirty ? String(values[fieldKey] ?? "") : displayDefault);
  // #2421 repair — "touch" must mean an actual EDIT, not merely tabbing
  // through on the way to another field (AC5/AC6's dirty flag is per-field
  // and per-touch, and a Select never fires onChange without a real pick, so
  // this buffered TextField is the one place a no-op focus+blur could mark a
  // field dirty and write it to every selected class with nothing changed).
  // Sets on every onChange, so it's true for the AC6 case too (clear() then
  // retype the SAME digits is still a real edit sequence, must still write).
  const [edited, setEdited] = useState(false);

  useEffect(() => {
    if (!isDirty) {
      setText(displayDefault);
      setEdited(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state.state, state.value]);

  // Cancelling the mixed-field warning dialog must leave this field showing
  // "Mixed" again, exactly like the Select path already does (its value is
  // fully controlled by state/dirty, so it reverts for free) — this
  // uncontrolled buffer needs the explicit nudge.
  useEffect(() => {
    if (mixedCancelSignal && mixedCancelSignal.key === fieldKey) {
      setText(displayDefault);
      setEdited(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mixedCancelSignal]);

  const placeholder = state.state === "mixed" && !isDirty ? "Mixed" : undefined;

  return (
    <FormControl size="small" sx={{ width: 240 }} data-testid={`bulk-config-${testIdBase}`}>
      <TextField
        type="number"
        size="small"
        label={label}
        placeholder={placeholder}
        value={text}
        disabled={saving}
        sx={{ width: 240 }}
        inputProps={{ min: 0, "data-testid": `bulk-${testIdBase}` }}
        onChange={(e) => {
          setEdited(true);
          setText(e.target.value);
        }}
        onBlur={() => {
          if (!edited) return;
          // Empty means clear (previously parseInt("") === NaN silently did
          // nothing at all, making a bulk-unset impossible): commit `null`.
          // The write side (ClassManager's bulkSaveClassConfig) is the one
          // that knows whether the field this buffer backs can actually
          // accept a cleared value on the wire.
          if (text === "") {
            onCommit(fieldKey, label, null, state.state);
            return;
          }
          const parsed = parseInt(text, 10);
          if (Number.isNaN(parsed)) return;
          onCommit(fieldKey, label, parsed, state.state);
        }}
      />
      <FormHelperText>{help}</FormHelperText>
    </FormControl>
  );
}

// A generic enum Select covering agree / mixed / not-applicable / dirty,
// shared by every enum-ish field below (error deduction scheme, T/Q
// topology+position+judge, section judges, result layout, DSHB override).
function BulkSelectField({
  testIdBase,
  label,
  help,
  fieldKey,
  state,
  dirty,
  values,
  saving,
  menuProps,
  onCommit,
  t,
  options,
  allowBlank,
  blankLabel,
  notSetValue,
  notSetLabelKey,
  notSetSelectable,
  width = 240,
}) {
  const isDirty = Boolean(dirty[fieldKey]);

  if (state.state === "na") {
    return (
      <FormControl size="small" sx={{ width }} data-testid={`bulk-config-${testIdBase}`}>
        <InputLabel id={`bulk-${testIdBase}-label`}>{label}</InputLabel>
        <Select
          labelId={`bulk-${testIdBase}-label`}
          label={label}
          value={NOT_APPLICABLE}
          disabled
          data-testid={`bulk-${testIdBase}`}
        >
          <MenuItem value={NOT_APPLICABLE}>
            <em>{t("ring.bulkAssign.notApplicable")}</em>
          </MenuItem>
        </Select>
        <FormHelperText>{t("ring.bulkAssign.notApplicableHelp")}</FormHelperText>
      </FormControl>
    );
  }

  // Deliberately NOT `value || fallback`: a real, agreed-on value of `false`
  // (e.g. every selected class has skipUsefUpload off) is falsy but must
  // still render as itself, not fall through to the "not set" sentinel.
  const isRealValue = (v) => v !== null && v !== undefined && v !== "";
  const fallback = allowBlank ? "" : notSetValue ?? NOT_SET;
  const shownValue = isDirty
    ? (isRealValue(values[fieldKey]) ? values[fieldKey] : fallback)
    : state.state === "agree"
      ? (isRealValue(state.value) ? state.value : fallback)
      : MIXED;

  // #2421 (defect 1's fix, generalized) — mirrors ClassDetailDrawer's own
  // stale-value passthrough: an agreed-on value that matches no option here
  // (a roster id since removed, a judge name no longer on the panel) must
  // still render AS ITSELF instead of MUI logging "out of range value" and
  // showing blank, which would misreport a real value as unset.
  const knownValues = new Set(options.map((opt) => opt.value));
  const showStalePassthrough =
    isRealValue(shownValue) && shownValue !== MIXED && !knownValues.has(shownValue);

  return (
    <FormControl size="small" sx={{ width }} data-testid={`bulk-config-${testIdBase}`}>
      <InputLabel id={`bulk-${testIdBase}-label`}>{label}</InputLabel>
      <Select
        labelId={`bulk-${testIdBase}-label`}
        label={label}
        value={shownValue}
        disabled={saving}
        data-testid={`bulk-${testIdBase}`}
        MenuProps={menuProps}
        onChange={(e) => onCommit(fieldKey, label, e.target.value, state.state)}
      >
        {state.state === "mixed" && !isDirty && (
          <MenuItem value={MIXED} disabled>
            <em>{t("ring.bulkAssign.mixed")}</em>
          </MenuItem>
        )}
        {allowBlank && (
          <MenuItem value="">
            <em>{blankLabel}</em>
          </MenuItem>
        )}
        {!allowBlank && notSetLabelKey && (
          <MenuItem value={notSetValue ?? NOT_SET} disabled={!notSetSelectable}>
            {t(notSetLabelKey)}
          </MenuItem>
        )}
        {showStalePassthrough && <MenuItem value={shownValue}>{shownValue}</MenuItem>}
        {options.map((opt) => (
          <MenuItem key={String(opt.value)} value={opt.value}>
            {opt.label}
          </MenuItem>
        ))}
      </Select>
      {help && <FormHelperText>{help}</FormHelperText>}
    </FormControl>
  );
}

// Boolean fields render as a Select (On/Off/Mixed/Not applicable) rather
// than a Switch: unlike ClassDetailDrawer's per-class Switch, this dialog
// needs a real third (mixed) and fourth (not-applicable) visual state, and
// MUI's Switch has no indeterminate state (unlike Checkbox) — a Select
// keeps every field in this dialog on the same agree/mixed/na/dirty
// machinery instead of a one-off Checkbox-indeterminate special case.
function BulkBooleanField({ testIdBase, label, help, fieldKey, state, dirty, values, saving, menuProps, onCommit, t }) {
  return (
    <BulkSelectField
      testIdBase={testIdBase}
      label={label}
      help={help}
      fieldKey={fieldKey}
      state={state}
      dirty={dirty}
      values={values}
      saving={saving}
      menuProps={menuProps}
      onCommit={onCommit}
      t={t}
      notSetValue={false}
      notSetLabelKey={undefined}
      options={[
        { value: false, label: t("ring.bulkAssign.off") },
        { value: true, label: t("ring.bulkAssign.on") },
      ]}
    />
  );
}
